import { immerable } from 'immer';
import { isString } from 'api/helpers';

import { IResolution } from 'api/types';
import { CameraModel, ICameraModel } from './camera.model';
import { IProjectModel, ProjectModel } from './project.model';

/* -------------------------------------------------------------------------------------------------
 * ICameraCCTVModel
 * -----------------------------------------------------------------------------------------------*/
export interface ICameraCCTVChannel {
    _id: string;
    number: string;
    // streamingUrl: string;
    hlsUrl: string;
    folder: string; //for frames
    ptzControl: {
        deviceId?: string;
        isAvailable: boolean;
    };
    name: string;
    installationDate: string;
    webrtcUrl: string;
    status: string;
    latestImage: {
        url: string;
        resolution: IResolution;
        datetime: string;
        date: string;
    };
    cameraCctv: string | ICameraCCTVModel;
}

/* -------------------------------------------------------------------------------------------------
 * ICameraCCTVModel
 * -----------------------------------------------------------------------------------------------*/
export interface ICameraCCTVModel {
    _id: string;
    name: string;
    installationDate: string;
    hlsUrl: string;
    status: string;
    webrtcUrl: string;

    // latestImage?: {
    //     url: string;
    //     resolution: IResolution;
    //     datetime: string;
    //     date: string;
    // };
    // ptzControl: {
    //     deviceId: string;
    //     isAvailable: boolean;
    // };
    timezone: string;
    lastUpdated: Date;
    qLiveUnit: string;
    camera: string | ICameraModel;
    project: string | IProjectModel;

    channels: ICameraCCTVChannel[];
    defaultChannel: ICameraCCTVChannel;
    angle: number;
    distance: number;
    fieldOfView: number;
    location: {
        name: string;
        latitude?: string;
        longitude?: string;
    };
    latestImage: {
        name: string;
        url: string;
    } | null;
}

/* -------------------------------------------------------------------------------------------------
 * CameraCCTVModel
 * -----------------------------------------------------------------------------------------------*/
export class CameraCCTVModel {
    [immerable] = true;

    _id: string;
    name: string;
    installationDate: string;
    hlsUrl: string;
    webrtcUrl: string;
    status: string;
    // latestImage?: {
    //     url: string;
    //     resolution: IResolution;
    //     datetime: string;
    //     date: string;
    // };
    timezone: string;
    lastUpdated: Date;
    qLiveUnit: string;
    // ptzControl: {
    //     deviceId: string;
    //     isAvailable: boolean;
    // };

    channels: ICameraCCTVChannel[];
    defaultChannel: ICameraCCTVChannel;
    angle: number;
    distance: number;
    fieldOfView: number;
    location: {
        name: string;
        latitude?: string;
        longitude?: string;
    };
    latestImage: {
        name: string;
        url: string;
    } | null;

    // Relationships
    camera: string | CameraModel;
    project: string | ProjectModel;

    constructor(data: Partial<ICameraCCTVModel>) {
        this._id = data._id ?? '';
        this.name = data.name ?? '';
        this.installationDate = data.installationDate ?? '';
        this.hlsUrl = data.hlsUrl ?? '';
        this.webrtcUrl = data.webrtcUrl ?? '';
        this.status = data.status ?? '';
        // this.latestImage = data.latestImage ?? null;
        this.timezone = data.timezone ?? '';
        this.lastUpdated = data.lastUpdated ?? null;
        this.qLiveUnit = data.qLiveUnit ?? '';
        // this.ptzControl = data.ptzControl ?? null;

        this.channels = data.channels ?? [];
        this.defaultChannel = data.defaultChannel ?? null;
        this.angle = data.angle ?? null;
        this.distance = data.distance ?? null;
        this.fieldOfView = data.fieldOfView ?? null;
        this.location = {
            name: data.location?.name ?? '',
            latitude: data.location?.latitude ?? '',
            longitude: data.location?.longitude ?? '',
        };

        this.latestImage = data.latestImage
            ? {
                  name: data.latestImage.name ?? '',
                  url: data.latestImage.url ?? '',
              }
            : null;

        if (data.camera) {
            if (isString(data.camera)) {
                this.camera = data.camera as string;
            } else {
                this.camera = CameraModel.fromObject(data.camera as ICameraModel);
            }
        }

        if (data.project) {
            if (isString(data.project)) {
                this.project = data.project as string;
            } else {
                this.project = ProjectModel.fromObject(data.project as IProjectModel);
            }
        }
    }

    static fromObject(data: Partial<ICameraCCTVModel>): CameraCCTVModel {
        const model = new CameraCCTVModel(data);

        return model;
    }

    static fromObjectList(data: Partial<ICameraCCTVModel>[]): CameraCCTVModel[] {
        return data.map((row) => CameraCCTVModel.fromObject(row));
    }

    static cloneFrom(data: Partial<ICameraCCTVModel>): CameraCCTVModel {
        const model = new CameraCCTVModel(data);

        return model;
    }

    toObject(): ICameraCCTVModel {
        const data: Partial<ICameraCCTVModel> = {
            _id: this._id,
            name: this.name,
            installationDate: this.installationDate || null,
            hlsUrl: this.hlsUrl || null,
            webrtcUrl: this.webrtcUrl || null,
            status: this.status,
            // latestImage: this.latestImage,
            timezone: this.timezone || null,
            lastUpdated: this.lastUpdated,
            qLiveUnit: this.qLiveUnit || null,
            // ptzControl: this.ptzControl ?? null,

            channels: this.channels ?? [],
            defaultChannel: this.defaultChannel ?? null,
            angle: this.angle,
            distance: this.distance,
            fieldOfView: this.fieldOfView,
            location: {
                name: this.location.name || null,
                latitude: this.location.latitude || null,
                longitude: this.location.longitude || null,
            },
            latestImage: this.latestImage
                ? {
                      name: this.latestImage.name,
                      url: this.latestImage.url,
                  }
                : null,
        };

        if (this.camera) {
            if (this.camera instanceof CameraModel) {
                data.camera = this.camera.toObject();
            } else {
                data.camera = this.camera;
            }
        }

        if (this.project) {
            if (this.project instanceof ProjectModel) {
                data.project = this.project.toObject();
            } else {
                data.project = this.project;
            }
        }

        return data as ICameraCCTVModel;
    }
}
